import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Stack, Button, Divider, Chip, Grid,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioOrcamentosServico from './EditPdfRelatorioOrcamentosServico';
import { cores, coresGrafico } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA  = cores.laranja;
const CINZA    = cores.chumbo;
const CINZA2   = cores.cinzaMedio;
const LARANJA2 = cores.laranjaEscuro;
const CORES    = coresGrafico;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, destaque }) => (
  <Box sx={{
    flex: 1, minWidth: 130, textAlign: 'center',
    border: `1px solid ${destaque ? LARANJA : '#DDD'}`,
    borderRadius: 1, p: 1.5,
    backgroundColor: destaque ? '#FFF3E0' : '#FAFAFA',
    borderLeft: `4px solid ${destaque ? LARANJA : CINZA}`,
  }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="subtitle1" fontWeight="bold" color={destaque ? LARANJA : CINZA}>{valor}</Typography>
  </Box>
);

// ── Título de seção ────────────────────────────────────────────────────────
const SecTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
    <Box sx={{ width: 4, height: 20, backgroundColor: LARANJA, borderRadius: 1 }} />
    <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>{children}</Typography>
  </Box>
);

// ── Tabela de Etapas (sem potência — não se aplica a serviço) ──────────────
const TabelaEtapas = ({ titulo, dados }) => (
  <Box mt={3}>
    {titulo && <Typography variant="body2" fontWeight="bold" color={LARANJA} mb={1}>{titulo}</Typography>}
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
          {['Etapa', 'Quantidade', 'Valor (R$)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((e, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell>{e.etapa}</TableCell>
            <TableCell>{fmtNum(e.quantidade)}</TableCell>
            <TableCell>{fmtBRL(e.valor)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ── Tabela de Tipos de Serviço ──────────────────────────────────────────────
const TabelaTipoServico = ({ dados }) => (
  <Box mt={3}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
          {['Tipo de Serviço', 'Quantidade', 'Valor (R$)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((g, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell>{g.grupo}</TableCell>
            <TableCell>{fmtNum(g.quantidade)}</TableCell>
            <TableCell>{fmtBRL(g.valor)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

export default function RelatorioOrcamentosServico({ data, type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const r = data.resumo;

  // ── Dados para gráficos ────────────────────────────────────────────────
  const dadosQtd = (data.graficoQuantidade || []).filter((d) => d.valor > 0)
    .map((d) => ({ name: d.etapa, value: d.valor }));

  const dadosPorOrigem = (data.porOrigem || []).map((g) => ({
    name: g.grupo,
    quantidade: g.tabela?.reduce((s, e) => s + e.quantidade, 0) || 0,
  }));

  const dadosPorTipoServico = (data.porTipoServico || []).map((g) => ({
    name: g.grupo,
    quantidade: g.quantidade,
  }));

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Orçamentos de Serviço']);
    sheet.push([]);
    sheet.push(['1. Orçamentos por Etapa']);
    sheet.push(['Etapa', 'Quantidade', 'Valor (R$)']);
    data.tabela.forEach((e) => sheet.push([e.etapa, e.quantidade, e.valor]));
    sheet.push([]);
    sheet.push(['2. Por Tipo de Serviço']);
    sheet.push(['Tipo de Serviço', 'Quantidade', 'Valor (R$)']);
    (data.porTipoServico || []).forEach((g) => sheet.push([g.grupo, g.quantidade, g.valor]));
    sheet.push([]);
    sheet.push(['3. Resumo']);
    sheet.push(['Ticket Médio (R$)', r.ticketMedioReais]);
    sheet.push(['Taxa de Conversão (%)', r.taxaConversao]);
    sheet.push(['Taxa de Perdidos (%)', r.taxaPerdidos]);
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamentos Serviço');
    XLSX.writeFile(wb, 'relatorio-orcamentos-servico.xlsx');
  };

  return (
    <Box mt={4}>
      {/* Barra de ações */}
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioOrcamentosServico(data, filtro)}>Exportar em PDF</Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: LARANJA2 } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {/* Filtros ativos */}
      {filtro?.servico && (
        <Chip label={`Serviço: ${filtro.servico}`} size="small" sx={{ mb: 2, backgroundColor: '#FFF3E0', color: LARANJA }} />
      )}

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <SecTitle>Resumo de Orçamentos de Serviço</SecTitle>
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
        <KpiCard label="Ticket Médio (R$)" valor={fmtBRL(r.ticketMedioReais)} destaque />
        <KpiCard label="Taxa de Conversão" valor={`${Number(r.taxaConversao || 0).toFixed(1)}%`} />
        <KpiCard label="Taxa de Perdidos"  valor={`${Number(r.taxaPerdidos || 0).toFixed(1)}%`} />
      </Stack>

      {/* ── Gráfico 1 — Quantidade por Etapa (pizza) ───────────────────── */}
      <SecTitle>Distribuição por Etapa</SecTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color={CINZA2} mb={1} textAlign="center">Quantidade por Etapa</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={dadosQtd} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {dadosQtd.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color={CINZA2} mb={1} textAlign="center">Quantidade por Tipo de Serviço</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={dadosPorTipoServico} dataKey="quantidade" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {dadosPorTipoServico.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>

      {/* ── Gráfico 2 — Origem do Atendimento ────────────────────────── */}
      {dadosPorOrigem.length > 0 && (
        <>
          <SecTitle>Orçamentos por Origem do Atendimento</SecTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dadosPorOrigem} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="quantidade" name="Orçamentos" radius={[4, 4, 0, 0]}>
                {dadosPorOrigem.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ── Tabela principal ──────────────────────────────────────────── */}
      <SecTitle>Orçamentos de Serviço por Etapa</SecTitle>
      <TabelaEtapas dados={data.tabela} />

      <Divider sx={{ my: 3 }} />

      {/* ── Segmentações ──────────────────────────────────────────────── */}
      <SecTitle>Segmentações</SecTitle>

      {data.porTipoServico?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Tipo de Serviço</Typography>
          <TabelaTipoServico dados={data.porTipoServico} />
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {data.porOrigem?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Forma de Atendimento</Typography>
          {data.porOrigem.map((g, i) => (
            <Box key={i} mt={2} pl={2} borderLeft={`3px solid ${CORES[i % CORES.length]}`}>
              <Typography variant="caption" fontWeight="bold" color={CINZA}>{g.grupo}</Typography>
              <TabelaEtapas dados={g.tabela} />
            </Box>
          ))}
        </>
      )}

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}